/**
 * College Queries Service
 * Database query functions for college data
 */

import { supabase } from "../../lib/supabase";
import { College } from "../../data/colleges";
import type {
  CollegeFilters,
  CollegeRow,
  SearchCollegesParams,
  SearchCollegesResponse,
  CollegeDetailData,
  FilterMetadata,
} from "./types";
import {
  transformDatabaseToCollege,
  applyClientSideFilters,
  localeToCampusType,
  parseMajors,
} from "./utils";
import { getErrorMessage } from "../../utils/errorUtils";

const COLLEGE_VIEW = "college_recommender_data" as const;

// ============================================
// Main Query Functions
// ============================================

/**
 * Get colleges with optional filters
 */
export async function getColleges(
  filters: CollegeFilters = {},
): Promise<College[]> {
  try {
    let query = supabase.from(COLLEGE_VIEW).select("*");

    if (filters.state) {
      query = query.eq("csc_stabbr", filters.state);
    }

    const { data, error } = await query.limit(1000);

    if (error) {
      console.error("❌ Error querying college view:", error);
      throw new Error(`Failed to fetch colleges: ${error.message}`);
    }

    if (!data || data.length === 0) return [];

    const colleges = (data as CollegeRow[]).map(transformDatabaseToCollege);

    let filteredColleges = applyClientSideFilters(colleges, filters);

    if (filters.tuitionRange) {
      const [minTuition, maxTuition] = filters.tuitionRange;
      filteredColleges = filteredColleges.filter((college) => {
        const tuition = filters.inStateOnly
          ? college.tuition.inState
          : college.tuition.outOfState;
        if (tuition === null || tuition === undefined) return true;
        return tuition >= minTuition && tuition <= maxTuition;
      });
    }

    return filteredColleges;
  } catch (error: unknown) {
    console.error("❌ Error fetching colleges:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Fetch colleges by specific IDs (optimized for favorites)
 */
export async function getCollegesByIds(
  collegeIds: string[],
): Promise<College[]> {
  try {
    if (!collegeIds || collegeIds.length === 0) return [];

    const numericIds = collegeIds
      .map((id) => parseInt(id.replace(/\D/g, ""), 10))
      .filter((id) => !isNaN(id));

    if (numericIds.length === 0) return [];

    const { data, error } = await supabase
      .from(COLLEGE_VIEW)
      .select("*")
      .in("csc_unitid", numericIds);

    if (error) {
      throw new Error(`Failed to fetch colleges by IDs: ${error.message}`);
    }

    if (!data) return [];

    return (data as CollegeRow[]).map(transformDatabaseToCollege);
  } catch (error: unknown) {
    console.error("❌ Error fetching colleges by IDs:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Search colleges with meta information (pagination)
 */
export async function searchCollegesWithMeta(
  params: SearchCollegesParams = {},
): Promise<SearchCollegesResponse> {
  try {
    const { page = 1, pageSize = 20, ...filters } = params;

    // Get all colleges with filters
    const allColleges = await getColleges(filters);

    // Apply pagination
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedColleges = allColleges.slice(startIndex, endIndex);

    return {
      data: paginatedColleges,
      meta: {
        total: allColleges.length,
        page,
        page_size: pageSize,
        total_pages: Math.ceil(allColleges.length / pageSize),
      },
    };
  } catch (error: unknown) {
    console.error("❌ Error searching colleges:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get single college details
 */
export async function getCollegeDetails(collegeId: string): Promise<College> {
  try {
    const colleges = await getCollegesByIds([collegeId]);
    if (colleges.length === 0) {
      throw new Error(`College not found: ${collegeId}`);
    }
    return colleges[0];
  } catch (error: unknown) {
    console.error("❌ Error fetching college details:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get comprehensive college detail data for the Detail Page
 */
export async function getCollegeDetailData(
  collegeId: string,
): Promise<CollegeDetailData> {
  try {
    const numericId = parseInt(collegeId.replace(/\D/g, ""), 10);
    if (isNaN(numericId)) throw new Error("Invalid college ID");

    const { data, error } = await supabase
      .from(COLLEGE_VIEW)
      .select("*")
      .eq("csc_unitid", numericId)
      .maybeSingle();

    if (error) throw new Error(`Failed to fetch college detail: ${error.message}`);

    const row: CollegeRow = data || {};

    const getInstitutionalType = (
      control: number | undefined,
    ): "Liberal Arts" | "Private" | "Public" | null => {
      if (control === 1) return "Public";
      if (control === 2) return "Private";
      return null;
    };

    const satVerbal = row.filled_satvr50 ? parseInt(row.filled_satvr50, 10) : null;
    const satMath = row.filled_satmt50 ? parseInt(row.filled_satmt50, 10) : null;
    const meanSat = satVerbal && satMath ? satVerbal + satMath : null;

    return {
      overview: {
        location: {
          city: row.csc_city || "",
          state: row.csc_stabbr || "",
        },
        institutionalType: getInstitutionalType(row.csc_control),
        acceptanceRate: row.filled_adm_rate || null,
        applicationFee: null,
      },
      academics: {
        meanGpa: row.estimated_gpa || null,
        meanAct: row.filled_actcm50 ? parseFloat(row.filled_actcm50) : null,
        meanSat,
      },
      admissions: {
        totalApplicants: null,
        acceptanceRate: row.filled_adm_rate || null,
      },
      cost: {
        inStateTuition: row.csc_cinsoff || null,
        outStateTuition: row.csc_cinson || null,
        netPriceAnnual: row.csc_netprice_any || null,
        percentStudentsWithAid: row.ipeds_any_grant_pct || null,
        averageAid: null,
      },
      deadline: {
        earlyDecision: null,
        earlyDecision2: null,
        earlyAction: null,
        regular: null,
      },
      studentLife: {
        undergradClassSize: null,
        internationalPercent: null,
        campusType: localeToCampusType(row.csc_locale || null),
        bestInRankings: null,
      },
    };
  } catch (error: unknown) {
    console.error("Error in getCollegeDetailData:", getErrorMessage(error));
    throw error;
  }
}

/**
 * Get filter metadata (states, majors, ranges)
 */
export async function getFilterMetadata(): Promise<FilterMetadata> {
  try {
    const [statesResult, majorsResult] = await Promise.all([
      supabase
        .from(COLLEGE_VIEW)
        .select("csc_stabbr")
        .not("csc_stabbr", "is", null),
      supabase
        .from(COLLEGE_VIEW)
        .select("csc_majors")
        .not("csc_majors", "is", null)
        .limit(100),
    ]);

    const states = [
      ...new Set((statesResult.data || []).map((r) => r.csc_stabbr)),
    ]
      .filter(Boolean)
      .sort() as string[];

    const allMajors = new Set<string>();
    (majorsResult.data || []).forEach((r) => {
      parseMajors(r.csc_majors).forEach((m) => allMajors.add(m));
    });

    return {
      states,
      majors: [...allMajors].sort(),
      tuitionRange: { min: 0, max: 80000 },
      satRange: { min: 800, max: 1600 },
      gpaRange: { min: 0, max: 4 },
      sizes: ["Small", "Medium", "Large"],
      campusTypes: ["Urban", "Suburban", "Rural"],
    };
  } catch (error: unknown) {
    console.error("Error in getFilterMetadata:", getErrorMessage(error));
    throw error;
  }
}
